/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Button,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  FormController,
  Typography,
  Tile,
  TileGroup,
  Alert,
  Skeleton,
  SkeletonLine,
  LoadingButton,
  Pagination,
  PaginationDisplayedItemCount,
} from '@one-thd/sui-atomic-components';
import { RadioButtonUnchecked, ConfirmedFilled } from '@one-thd/sui-icons';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  MfaProvider,
  useMfaContext,
} from '@thd-olt-component-react/mfa-passkey-drawer';
import { AxiosError } from 'axios';
import { IUserData, usersApi } from '../../../api/b2b/users';
import { sortUsers, reconcileAuthorizedUsers } from '../../../utils/user';
import { ERR_GENERIC_WITH_CANCEL } from '../../../constants/response-error-messages';
import { CHANGES_SAVED_MSG } from '../../../constants/response-success-messages';
import { MFA_ERR_000 } from '../../../constants/error-codes';
import {
  PERMISSIONS_USERS_PAGE_SIZE,
  PAGINATION_THRESHOLD,
} from '../../../constants/general';
import {
  getUsersForTenderRequest,
  updateUsersForTenderRequest,
} from '../../../api/b2b/payments';
import { createUpdateUsersForTenderRequestPayload } from '../../../utils/update-permissions';
import { getPaymentsErrorCode } from '../../../utils/errors';
import { TPaymentCardErrorResponse } from '../../../models/common/credit-cards';
import { IDrawerBasicProps } from '../../../models/common/drawer';
import { selectPhaPermissionsCompletionAdvisoryAnalytics } from '../../../utils/payments-analytics';
import {
  trackUserPermissionsUpdateInitiated,
  trackUserPermissionsUpdateSuccess,
  trackUserPermissionsUpdateFailure,
  trackMfaFlowInitiated,
  trackMfaFlowSuccess,
  trackMfaFlowFailure,
} from '../../../utils/passkey-mfa-tracking';

export interface ISelectPhaPermissions extends IDrawerBasicProps {
  onSuccess: (message: string) => void;
  paymentId: string;
}

const SelectPhaPermissionsContent = ({
  hideDrawer,
  onSuccess,
  paymentId,
}: ISelectPhaPermissions) => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const initiallySelectedUsers = useRef<string[]>([]);
  const [allUsers, setAllUsers] = useState<IUserData[]>([]);
  const [isError, setIsError] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [page, setPage] = useState(1);
  const totalUsers = allUsers.length;

  const totalPageCount = Math.ceil(totalUsers / PERMISSIONS_USERS_PAGE_SIZE);
  const isPaginationVisible = totalUsers > PAGINATION_THRESHOLD;
  const usersPerPage = useMemo(
    () =>
      allUsers.slice(
        PERMISSIONS_USERS_PAGE_SIZE * (page - 1),
        PERMISSIONS_USERS_PAGE_SIZE * page,
      ),
    [allUsers, page],
  );

  const handleChangePage = (_event: object, value: number) => {
    setPage(value);
  };

  const isMfaProtectedEnabled = !!useConfigService('MFA_PROTECTED_ENABLED');
  const isMfaDrawerEnabled = !!useConfigService('PTC_MFA_DRAWER_ENABLED');
  const { triggerFlow } = useMfaContext();

  const handleTileChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    newFormats: string[],
  ) => {
    setSelectedUsers(newFormats);
  };

  // Select All temporarily removed per Srikanth/Gunjit decision (2026-07-21) -
  // fast-follow to be reintroduced with backend support. Uncomment to restore:
  //
  // const handleSelectAll = () => {
  //   if (allUsers.length === selectedUsers.length) {
  //     setSelectedUsers([]);
  //   } else {
  //     const allUserIds = allUsers.map((user) => user.userId.toString());
  //     setSelectedUsers(allUserIds);
  //   }
  // };
  //
  // const getSelectAllText = () => {
  //   if (allUsers.length === selectedUsers.length) {
  //     return `Deselect All (${selectedUsers.length} selected)`;
  //   }
  //   if (selectedUsers.length > 0) {
  //     return `Select All (${selectedUsers.length} selected)`;
  //   }
  //   return 'Select All';
  // };

  useEffect(() => {
    const fetchUsers = async (): Promise<{
      users: IUserData[];
      hadPageFailures: boolean;
    }> => {
      const firstPageResponse = await usersApi.getUsers();
      const totalPages =
        Number(firstPageResponse.data?.paginationInfo?.totalPages) || 1;

      const remainingPageResults = await Promise.allSettled(
        Array.from({ length: totalPages - 1 }, (_unused, index) =>
          usersApi.getUsers({ page: index + 2 }),
        ),
      );

      const succeededResponses = remainingPageResults
        .filter((result) => result.status === 'fulfilled')
        .map(
          (result) =>
            (
              result as PromiseFulfilledResult<
                Awaited<ReturnType<typeof usersApi.getUsers>>
              >
            ).value,
        );
      const hadPageFailures = remainingPageResults.some(
        (result) => result.status === 'rejected',
      );

      const users = [firstPageResponse, ...succeededResponses].flatMap(
        (response) => response.data?.users || [],
      );
      return {
        users: users.filter(
          (user: IUserData) => user.role.toUpperCase() !== 'RUNNER',
        ),
        hadPageFailures,
      };
    };

    const fetchPreExistingUsers = async (): Promise<string[]> => {
      const { userIds } = await getUsersForTenderRequest(paymentId);
      return userIds;
    };

    setIsError(false);
    setLoadingUsers(true);

    const usersPromise = fetchUsers().then(({ users, hadPageFailures }) => {
      const sortedUsers = sortUsers(users);
      setAllUsers(sortedUsers);
      if (hadPageFailures) {
        handleError();
      }
      return sortedUsers;
    });
    const authorizedUserIdsPromise = fetchPreExistingUsers();

    Promise.all([usersPromise, authorizedUserIdsPromise])
      .then(([sortedUsers, authorizedUserIds]) => {
        const reconciledUserIds = reconcileAuthorizedUsers(
          sortedUsers,
          authorizedUserIds,
        );
        initiallySelectedUsers.current = [...reconciledUserIds];
        setSelectedUsers([...reconciledUserIds]);
      })
      .catch(() => {
        handleError();
      })
      .finally(() => {
        setLoadingUsers(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSuccess = () => {
    selectPhaPermissionsCompletionAdvisoryAnalytics(CHANGES_SAVED_MSG.message);
    onSuccess(CHANGES_SAVED_MSG.message);
    hideDrawer();
  };
  const handleError = () => {
    setIsError(true);
  };

  const handleSaveChanges = () => {
    const request = createUpdateUsersForTenderRequestPayload(
      selectedUsers,
      initiallySelectedUsers.current,
    );

    if (
      !request.updateUsersForTenderRequest?.authorizeUsers &&
      !request.updateUsersForTenderRequest?.deauthorizeUsers
    ) {
      hideDrawer();
      return Promise.resolve();
    }

    trackUserPermissionsUpdateInitiated({
      usersCount: selectedUsers.length,
      paymentId,
    });

    setIsError(false);
    return updateUsersForTenderRequest(paymentId, request)
      .then(({ success }) => {
        if (!success) {
          trackUserPermissionsUpdateFailure({
            errorCode: 'UPDATE_FAILED',
            usersCount: selectedUsers.length,
            paymentId,
          });
          handleError();
          return;
        }
        trackUserPermissionsUpdateSuccess(selectedUsers.length, {
          paymentId,
        });
        handleSuccess();
      })
      .catch((error: AxiosError<TPaymentCardErrorResponse>) => {
        if (!error) {
          trackUserPermissionsUpdateFailure({
            errorCode: 'UNKNOWN_ERROR',
            usersCount: selectedUsers.length,
            paymentId,
          });
          handleError();
          return;
        }

        if (error.response?.status === 403 && isMfaProtectedEnabled) {
          const errorCode = getPaymentsErrorCode(error);
          if (errorCode === MFA_ERR_000) {
            const mfaEncodedAction =
              error.response?.data?.paymentCards?.errors?.[0]?.encodedAction;
            if (mfaEncodedAction && isMfaDrawerEnabled) {
              trackMfaFlowInitiated('passkey-drawer', {
                action: 'UPDATE_USER_PERMISSIONS',
                usersCount: selectedUsers.length,
              });
              triggerFlow(
                mfaEncodedAction,
                () => {
                  trackMfaFlowSuccess('passkey-drawer', {
                    action: 'UPDATE_USER_PERMISSIONS',
                    usersCount: selectedUsers.length,
                  });
                  handleSaveChanges();
                },
                () => {
                  trackMfaFlowFailure('passkey-drawer', {
                    action: 'UPDATE_USER_PERMISSIONS',
                    errorCode: 'USER_CANCELLED_MFA',
                    usersCount: selectedUsers.length,
                  });
                  setIsSaveLoading(false);
                },
              );
              return;
            }
          }
        }

        trackUserPermissionsUpdateFailure({
          errorCode: getPaymentsErrorCode(error) || 'UNKNOWN',
          httpStatus: error.response?.status,
          usersCount: selectedUsers.length,
          paymentId,
        });
        handleError();
      });
  };

  const SkeletonLoader = () => {
    return (
      <div className="sui-ml-[-24px]">
        <Skeleton className="sui-grow" data-testid="skeleton-loader">
          <SkeletonLine numberOfLines={3} />
        </Skeleton>
      </div>
    );
  };

  return (
    <>
      <DrawerHeader
        title={`Users (${totalUsers})`}
        onClose={() => {
          hideDrawer();
        }}
      />
      <DrawerBody>
        <div className="sui-flex sui-flex-col sui-justify-between sui-h-full">
          <div className="sui-mb-4">
            <div className="sui-mb-2">
              <Typography variant="body-base" weight="bold">
                Select which users should have permissions to use this line of
                credit.
              </Typography>
              <Typography>
                You may grant permissions to Admins and Purchasers on your
                account to use this line of credit when checking out on
                HomeDepot.com. The balance of this line of credit will be
                available to all users you grant permissions to.
              </Typography>
            </div>
            {/* Select All temporarily removed per Srikanth/Gunjit decision (2026-07-21) - fast-follow with backend support. Uncomment to restore:
            <div className="sui-mb-4">
              <Link
                component="button"
                onClick={handleSelectAll}
                data-testid="select-text"
              >
                {getSelectAllText()}
              </Link>
            </div>
            */}
            {loadingUsers && <SkeletonLoader />}
            {!loadingUsers && (
              <FormController fullWidth>
                <TileGroup
                  className="sui-grid sui-grid-cols-1 sui-gap-4"
                  value={selectedUsers}
                  onChange={handleTileChange}
                  aria-label="permissions selection"
                  disableExclusive
                  data-testid="permissions-tiles"
                >
                  {usersPerPage.map((user) => (
                    <Tile
                      key={user.userId.toString()}
                      value={user.userId.toString()}
                      aria-label={`Select ${user.firstName} ${user.lastName}`}
                      grow
                    >
                      <div className="sui-flex sui-flex-col sui-w-full">
                        <div className="sui-flex sui-justify-between sui-items-center sui-w-full">
                          <Typography className="sui-truncate sui-text-left">
                            {user.firstName + ' ' + user.lastName}
                          </Typography>
                          <div className="sui-ml-4 sui-flex sui-justify-end">
                            {selectedUsers.includes(user.userId.toString()) ? (
                              <ConfirmedFilled
                                size="small"
                                color="primary"
                                data-testid="confirmed-checkbox"
                              />
                            ) : (
                              <RadioButtonUnchecked
                                size="small"
                                data-testid="radio-button-unchecked"
                              />
                            )}
                          </div>
                        </div>
                        <div className="sui-w-full">
                          <Typography
                            variant="body-sm"
                            color="subtle"
                            className="sui-text-left"
                          >
                            {user.role}
                          </Typography>
                        </div>
                      </div>
                    </Tile>
                  ))}
                </TileGroup>
              </FormController>
            )}
            {!loadingUsers && isPaginationVisible && (
              <div
                className="sui-flex sui-flex-col sui-items-center sui-gap-y-2 sui-mt-4"
                data-testid="PaginationWrap"
              >
                <Pagination
                  count={totalPageCount}
                  page={page}
                  onChange={handleChangePage}
                />
                <PaginationDisplayedItemCount
                  data-testid="pagination-item-count"
                  page={page}
                  itemsPerPage={PERMISSIONS_USERS_PAGE_SIZE}
                  items={totalUsers}
                />
              </div>
            )}
          </div>
          {isError && (
            <div
              data-testid="select-pha-drawer-alert"
              className="sui-mb-[-18px]"
            >
              <Alert status="error">{ERR_GENERIC_WITH_CANCEL.message}</Alert>
            </div>
          )}
        </div>
      </DrawerBody>
      <DrawerFooter>
        <div className="sui-flex sui-flex-col sui-gap-4">
          <LoadingButton
            fullWidth
            loading={isSaveLoading}
            variant="primary"
            onClick={() => {
              setIsSaveLoading(true);
              handleSaveChanges().finally(() => {
                setIsSaveLoading(false);
              });
            }}
          >
            Save Changes
          </LoadingButton>
          <Button
            fullWidth
            variant="secondary"
            onClick={() => {
              setIsError(false);
              hideDrawer();
            }}
          >
            Cancel
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};

export const SelectPhaPermissions = (props: ISelectPhaPermissions) => (
  <MfaProvider>
    <SelectPhaPermissionsContent {...props} />
  </MfaProvider>
);
